// ============================================================
// MOONSHINE MASTERS — moonshineYard.js        THE SHARED YARD (co-op step 2)
//
// caP'n, 2026-10-24: "yes <33" to one still for everyone - one tub, one row of jars, one fire.
//
// ⚑ THE KEEPER. On a server with friends, the brewer who got there FIRST keeps the yard (lowest `j` on the
//   brewers rows, moonshineBrewers.js; a tie goes to the lower uid - everyone works it out the same way). It is
//   HER save's yard everyone works: her tub, barrels, still, shelf and parts. When she leaves, the next one in
//   keeps the yard (theirs). Nobody's save is ever written by anybody else. (`j` is fixed per visit -
//   moonshineBrewers.arrivedAt - so changing your body mid-run doesn't hand the yard away.)
// ⚑ WHAT STAYS YOURS: your satchel, your jars, your Moon Coin, your contracts, your tutorial - and your OWN
//   yard, which waits for you (your still doesn't drip or blow while you're at a friend's: TICK `hold`).
// ⚑ HOW IT FLOWS (keeper-authoritative, the Snake Eyes host pattern):
//     games/moonshine/servers/{sid}/yard          { y, hn, k, m, ts }   the keeper's yard (JSON) · her clock · her uid · the yard's news
//     games/moonshine/servers/{sid}/yardQ/{push}  { u, n, k, a, ts }    a guest's hands: one action (JSON), for keeper k ->
//                                                 the keeper CLAIMS it (a transaction: exactly one keeper ever plays it), then plays it
//     games/moonshine/gifts/{uid}/{id}            { jar, from, ts }     your share of a run you brewed together (waits if you're away);
//                                                 the id comes from BLEND, so a resend overwrites; you CLAIM it before it's yours
//   The keeper's yard is removed when she disconnects (onDisconnect).
//   JSON strings on the wire: the Realtime Database eats nulls in arrays (the barrels) and refuses `undefined`.
// ⚑ CLOCKS: the keeper stamps her clock (hn) beside the SERVER's (ts); a guest slides the yard's times onto his own
//   through the server's clock (shiftYard) - a yard published 40 s ago still lands on the right second.
// ⚑ YARD NEWS ONLY: the keeper shares only lines the reducer marked msg.yard (her satchel / catches / gifts stay hers).
// ⚑ JARS GO ROUND THE CIRCLE: a run blended with friends on the server is shared round-robin, keeper first;
//   cursed jars stay with the keeper (nobody gets handed poison).
// ⚑ THE TILL (caP'n 2026-10-24): the Moon Coin and the contract book are the yard's while you're together - anyone
//   spends from it, anyone delivers their own jars into it, a contract done together is ticked in everyone's book.
// ⚑ HANDS IN THE POST (2026-10-25): a satchel or jars handed to a friend's yard ride in the save (s.hands) until the keeper's
//   receipt (servers/{sid}/yardDone/{me}/{id}) lands - closing the tab mid-send loses nothing (see the hook below).
// ⚠ PLACEHOLDERS (scaffolding first): the security posture is the games umbrella (any signed-in soul can write here) -
//   hardened later, code before rules. The woods are shared too: moonshineWoods.js.
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ref, set, push, get, remove, onValue, onChildAdded, onDisconnect, runTransaction, serverTimestamp } from "firebase/database";
import { db } from "./firebase";
import { serverNow } from "./ieSocial";
import { MOONSHINE_BASE } from "./moonshineOnline";
import { brewersPath } from "./moonshineBrewers";
import { yardOf, withYard, cleanYard, shiftYard, YARD_ACTIONS, pickJars, heldProofs } from "./moonshine";
import { contractOf } from "./missions";

export const yardPath = (sid) => `${MOONSHINE_BASE}/servers/${sid}/yard`;
export const yardQPath = (sid) => `${MOONSHINE_BASE}/servers/${sid}/yardQ`;
export const giftsPath = (uid) => `${MOONSHINE_BASE}/gifts/${uid}`;
// the keeper's receipts for hand-overs she has played: servers/{sid}/yardDone/{handerUid}/{handId} = server time (the hander clears it)
export const yardDonePath = (sid, uid) => `${MOONSHINE_BASE}/servers/${sid}/yardDone/${uid}`;
export const newHandId = () => "h" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

const STALE_MS = 75000;        // ⟵ DIAL  a brewer silent this long has gone home (= moonshineBrewers). Long on purpose: a browser
                               // slows a background tab to one beat a minute - alt-tabbing must not hand the yard away (a real
                               // goodbye is instant: onDisconnect removes the row).
const HAND_WAIT_MS = 90000;    // ⟵ DIAL  a hand-over nobody has confirmed after this long is taken back (the keeper had gone quiet)
const HAND_CHECK_MS = 15000;   // ⟵ DIAL  how often the post is checked
const INTENT_MAX_MS = 15000;   // ⟵ DIAL  a guest's action older than this is dropped (it was meant for another moment)
const parse = (x) => { try { return typeof x === "string" ? JSON.parse(x) : null; } catch (e) { return null; } };

// ── WHO'S HERE, AND WHO KEEPS THE YARD ──
function useCrew(sid, uid) {
  const [crew, setCrew] = useState([]);              // [{ uid, name, j }] live brewers, me included, keeper first
  useEffect(() => {
    setCrew([]);
    if (!sid || !uid) return undefined;
    let raw = {};
    const apply = () => {
      const now = serverNow();
      const live = Object.entries(raw).filter(([, r]) => r && Number.isFinite(r.j) && r.ts && now - r.ts <= STALE_MS)
        .map(([id, r]) => ({ uid: id, name: String(r.n || "Brewer").slice(0, 24), j: r.j }))
        .sort((a, b) => a.j - b.j || (a.uid < b.uid ? -1 : 1));
      setCrew((prev) => (prev.map((c) => c.uid + c.name).join() === live.map((c) => c.uid + c.name).join() ? prev : live));
    };
    const off = onValue(ref(db, brewersPath(sid)), (snap) => { raw = snap.val() || {}; apply(); }, () => {});
    const t = setInterval(apply, 5000);
    return () => { off(); clearInterval(t); };
  }, [sid, uid]);
  return crew;
}
// claim a child exactly once across every client: delete it in a transaction; only the one that deleted it plays it
const claim = (r) => runTransaction(r, (cur) => (cur ? null : undefined)).then((res) => !!(res && res.committed)).catch(() => false);
const nm = (x) => String(x || "A friend").slice(0, 24);

// the same, for a child I'm NOT already listening to (a transaction on an unwatched path is first run against "nothing", and a
// handler that aborts on "nothing" never asks the server): hold a listener open while the transaction runs, so it sees the truth
const claimFresh = (r) => new Promise((resolve) => {
  let off = () => {}, done = false;
  const fin = (v) => { if (!done) { done = true; resolve(v); } };
  off = onValue(r, (snap) => {
    if (!snap.exists()) { fin(false); off(); return; }
    claim(r).then(fin).finally(() => off());
  }, () => fin(null));
  setTimeout(() => fin(null), 8000);
});

// ── THE YARD HOOK ── returns { mode, keeperName, keeperUid, ready, helpers, view, act }
//   mode   "solo" (offline, or alone)  ·  "keeper" (it's my yard, friends are working it)  ·  "guest" (a friend's yard)
//   ready  a guest has the keeper's yard in hand (until then the yard's stations wait)
//   view   the game state the screen should draw (mine, with the keeper's yard laid over it when I'm a guest)
//   act    dispatch, routed: a guest's yard actions go to the keeper; everything else is mine
export function useYard({ sid, uid, name, s, dispatch, me = null }) {
  const crew = useCrew(sid, uid);
  const meIn = crew.some((c) => c.uid === uid);
  const keeper = meIn && crew.length ? crew[0] : null;
  const mode = !sid || !uid || !keeper || crew.length < 2 ? "solo" : keeper.uid === uid ? "keeper" : "guest";
  const others = useMemo(() => crew.filter((c) => c.uid !== uid).map((c) => c.uid), [crew, uid]);
  const sRef = useRef(s); sRef.current = s;
  const [remote, setRemote] = useState(null);
  const ctx = useRef({}); ctx.current = { mode, keeper, others, name, sid, uid, ready: !!remote };

  // ── KEEPER: publish my yard whenever it changes (it only changes on an action or a still event) ──
  const yardStr = useMemo(() => (mode === "keeper" ? JSON.stringify(yardOf(s)) : null), [mode, s]);
  const news = s.msg && s.msg.yard ? s.msg : null;
  const newsRef = useRef(null);
  if (mode === "keeper" && news && (!newsRef.current || newsRef.current.id !== news.id)) newsRef.current = { id: news.id, text: news.text, icon: news.icon || null };
  const newsId = newsRef.current ? newsRef.current.id : 0;
  useEffect(() => {
    if (mode !== "keeper" || !yardStr) return;
    set(ref(db, yardPath(sid)), { y: yardStr, hn: Date.now(), k: uid, m: newsRef.current, ts: serverTimestamp() }).catch(() => {});
  }, [mode, sid, uid, yardStr, newsId]);
  useEffect(() => {            // my yard leaves with me
    if (mode !== "keeper") return undefined;
    const r = ref(db, yardPath(sid));
    onDisconnect(r).remove().catch(() => {});
    return () => { onDisconnect(r).cancel().catch(() => {}); };
  }, [mode, sid]);

  // ── KEEPER: play my friends' hands - each one claimed first, so it's played exactly once by exactly one keeper ──
  const played = useRef(new Set());
  useEffect(() => {
    if (mode !== "keeper") return undefined;
    const off = onChildAdded(ref(db, yardQPath(sid)), (snap) => {
      if (played.current.has(snap.key)) return;
      played.current.add(snap.key);
      const v = snap.val() || {}, a = parse(v.a);
      claim(snap.ref).then((mine) => {
        if (!mine || !a || !YARD_ACTIONS.includes(a.type)) return;
        const me = ctx.current;
        if (a.type !== "STOCK" && a.type !== "DELIVER_JARS") {        // a satchel or someone's jars are never thrown away (misfit jars go home); everything else is for its moment
          if (v.k && v.k !== me.uid) return;                           // meant for another keeper's yard
          if (v.ts && serverNow() - v.ts > INTENT_MAX_MS) return;
        }
        const extra = a.type === "BLEND" ? { share: me.others } : a.type === "DELIVER_JARS" ? { by: v.u, key: snap.key } : {};   // whose jars: the sender, not the payload; the key names the jars going home
        dispatch({ ...a, ...extra, now: Date.now(), who: nm(v.n) });   // MY clock, not theirs
        if ((a.type === "STOCK" || a.type === "DELIVER_JARS") && v.u && /^[A-Za-z0-9_-]{1,128}$/.test(String(v.u)))   // the receipt: "played" - so the hander knows it landed
          set(ref(db, `${yardDonePath(sid, v.u)}/${snap.key}`), serverTimestamp()).catch(() => {});
      });
    }, () => {});
    return () => off();
  }, [mode, sid, dispatch]);

  // ── ANYONE: hand out the jars I owe (each has its own id: a resend overwrites, never doubles) ──
  const sending = useRef(false);
  useEffect(() => {
    if (!uid || !s.outbox || !s.outbox.length || sending.current) return;
    sending.current = true;
    const batch = s.outbox.slice();
    Promise.allSettled(batch.map((o) => set(ref(db, `${giftsPath(o.to)}/${o.id}`), { jar: JSON.stringify(o.jar), from: nm(name), ts: serverTimestamp() }).then(() => o.id)))
      .then((rs) => { const ids = rs.filter((r) => r.status === "fulfilled").map((r) => r.value); if (ids.length) dispatch({ type: "OUTBOX_SENT", ids }); })
      .finally(() => { sending.current = false; });
  }, [uid, name, s.outbox, dispatch]);

  // ── GUEST: HANDS IN THE POST (the pinned "hand-over in flight" bug, fixed 2026-10-25) ──
  // A satchel or jars I hand a friend's yard leave my hands at once but ride in `s.hands` (saved) until the keeper's receipt lands.
  //   1. SEND: each hand for THIS server is written once, under its own id (create-if-absent: a resend after a reload can't double it).
  //   2. RECEIPT: the keeper writes yardDone/{me}/{id} after she plays it; I clear the receipt and drop the hand.
  //   3. TAKE BACK: a hand nobody confirmed (the keeper went quiet / I'm on another server now / the write never landed) is claimed
  //      back off the queue; only if it was still unplayed do the items come home. Never both, never neither.
  const hands = s.hands || [];
  const handsRef = useRef(hands); handsRef.current = hands;
  const sent = useRef(new Map());                        // hand id -> "inflight" | "acked" (this visit only: a reload forgets, and resends)
  useEffect(() => {
    if (!me || !sid) return;
    for (const h of hands) {
      if (h.by !== me || h.sid !== sid || sent.current.has(h.id)) continue;
      sent.current.set(h.id, "inflight");
      const action = h.kind === "STOCK" ? { type: "STOCK", items: h.items } : { type: "DELIVER_JARS", id: h.ct, jars: h.jars };
      runTransaction(ref(db, `${yardQPath(sid)}/${h.id}`), (cur) => (cur ? undefined : { u: me, n: nm(name), k: ctx.current.keeper ? ctx.current.keeper.uid : null, a: JSON.stringify(action), ts: serverTimestamp() }), { applyLocally: false })
        .then(() => sent.current.set(h.id, "acked"))
        .catch(() => { sent.current.delete(h.id); dispatch({ type: "HAND_BACK", id: h.id }); });   // refused outright: it never left
    }
  }, [hands, me, sid, name, dispatch]);
  useEffect(() => {                                       // receipts for my hand-overs on this server
    if (!me || !sid) return undefined;
    const off = onChildAdded(ref(db, yardDonePath(sid, me)), (snap) => {
      dispatch({ type: "HAND_DONE", id: snap.key });
      remove(snap.ref).catch(() => {});
    }, () => {});
    return () => off();
  }, [me, sid, dispatch]);
  const settling = useRef(new Set());
  useEffect(() => {                                       // the post office: take back what nobody confirmed
    if (!me) return undefined;
    const check = () => {
      for (const h of handsRef.current) {
        if (h.by !== me || settling.current.has(h.id) || sent.current.get(h.id) === "inflight") continue;
        if (h.sid === sid && Date.now() - h.at < HAND_WAIT_MS) continue;     // the keeper still has time
        settling.current.add(h.id);
        (async () => {
          try {
            const took = await claimFresh(ref(db, `${yardQPath(h.sid)}/${h.id}`));       // true: it was still sitting there, now mine again
            if (took === null) return;                                                    // couldn't tell (offline): try again next time
            if (took) { dispatch({ type: "HAND_BACK", id: h.id }); return; }
            const d = await get(ref(db, `${yardDonePath(h.sid, me)}/${h.id}`));           // not in the queue: played (a receipt) or never landed (none)
            if (d.exists()) { dispatch({ type: "HAND_DONE", id: h.id }); remove(d.ref).catch(() => {}); }
            else dispatch({ type: "HAND_BACK", id: h.id });
          } catch (e) { /* try again next time */ } finally { settling.current.delete(h.id); }
        })();
      }
    };
    check();
    const t = setInterval(check, HAND_CHECK_MS);
    return () => clearInterval(t);
  }, [me, sid, dispatch]);

  // ── ANYONE: my share of a run, from a friend's yard - claimed first (two tabs of me can't both take it) ──
  const gotGift = useRef(new Set());
  useEffect(() => {
    if (!uid) return undefined;
    const off = onChildAdded(ref(db, giftsPath(uid)), (snap) => {
      if (gotGift.current.has(snap.key)) return;
      gotGift.current.add(snap.key);
      const v = snap.val() || {}, jar = parse(v.jar);
      claim(snap.ref).then((mine) => { if (mine && jar) dispatch({ type: "GIFT_JAR", jar, from: v.from }); });
    }, () => {});
    return () => off();
  }, [uid, dispatch]);

  // ── GUEST: the keeper's yard, on my clock ──
  const lastNews = useRef(null);
  useEffect(() => {
    setRemote(null); lastNews.current = null;
    if (mode !== "guest" || !keeper) return undefined;
    const off = onValue(ref(db, yardPath(sid)), (snap) => {
      const v = snap.val();
      if (!v || v.k !== keeper.uid) return;                        // an old keeper's yard: wait for the new one
      const y = cleanYard(parse(v.y));
      if (!y) return;
      // her clock -> the server's -> mine (the yard may have been published a while ago: hn and ts are the same moment)
      const ms = Number.isFinite(v.hn) && Number.isFinite(v.ts) ? (v.hn - v.ts) + (serverNow() - Date.now()) : 0;
      setRemote(shiftYard(y, ms));
      const m = v.m, key = m && `${v.k}:${m.id}`;
      if (m && m.text && lastNews.current !== null && key !== lastNews.current) dispatch({ type: "NOTE", text: String(m.text).slice(0, 200), icon: m.icon || null });
      lastNews.current = key || "";
    }, () => {});
    return () => off();
  }, [mode, sid, keeper && keeper.uid, dispatch]);   // eslint-disable-line react-hooks/exhaustive-deps

  // a guest draws the keeper's yard over mine. fuzzLeft (a paused fuzz clock) is a YARD_KEY since 2026-10-31, so the keeper's comes with it.
  const view = useMemo(() => (mode === "guest" && remote ? withYard(s, remote) : s), [mode, remote, s]);
  const remoteRef = useRef(null); remoteRef.current = remote;

  // ── GUEST: a contract the crew finishes while I'm here is checked off in MY book too (caP'n 2026-10-24) ──
  const lastBook = useRef(null);
  const keeperUid = keeper ? keeper.uid : null;
  useEffect(() => {
    if (mode !== "guest" || !remote) { lastBook.current = null; return; }
    const book = remote.contracts || {}, was = lastBook.current && lastBook.current.k === keeperUid ? lastBook.current.book : null;
    lastBook.current = { k: keeperUid, book };
    if (!was) return;                                        // what was done before I walked in (or in a NEW keeper's book) isn't mine to tick
    for (const [id, st] of Object.entries(book)) if (st === "done" && was[id] !== "done") dispatch({ type: "CONTRACT_DONE", id });
  }, [mode, remote, keeperUid, dispatch]);

  // ── ROUTING MY HANDS ──
  const act = useCallback((a) => {
    const c = ctx.current;
    if (!a) return;
    if (c.mode === "guest") {
      if (a.type === "TICK") { dispatch({ ...a, hold: true }); return; }          // my own still waits for me
      const send = (action) => push(ref(db, yardQPath(c.sid)), { u: c.uid, n: nm(c.name), k: c.keeper ? c.keeper.uid : null, a: JSON.stringify(action), ts: serverTimestamp() });
      if (a.type === "TUT_GIFT") {                                                 // Willie's sack (the tutorial, together): I have no yard here, so it goes on HER shelf
        dispatch({ ...a, items: {} });                                             // marks my sack as given - without putting it in the pantry the keeper's yard hides
        const items = { ...a.items };
        if (Object.keys(items).length) dispatch({ type: "HAND_ADD", hand: { id: newHandId(), sid: c.sid, by: c.uid, kind: "STOCK", items, at: Date.now() } });   // rides in the save until she confirms, like any satchel
        return;
      }
      if (a.type === "BANK") {                                                     // my satchel goes on HER shelf
        const items = { ...sRef.current.bag };
        if (!Object.keys(items).length) return;
        // the yard calls BANK every frame you stand in it with a full satchel: empty it HERE, now, so it's sent once
        sRef.current = { ...sRef.current, bag: {} };
        dispatch({ type: "HAND_ADD", hand: { id: newHandId(), sid: c.sid, by: c.uid, kind: "STOCK", items, at: Date.now() } });   // rides in the save until she confirms
        dispatch({ type: "BANK_AWAY", keeper: c.keeper && c.keeper.name });
        return;
      }
      if (a.type === "DELIVER") {                                                  // MY jars, THEIR book, the SHARED till
        const ct = contractOf(a.id), book = remoteRef.current ? remoteRef.current.contracts || {} : {};
        if (!ct || book[ct.id] !== "accepted") return;
        const idx = pickJars(sRef.current.jars, ct.req, heldProofs({ contracts: book }, ct.id));   // keep a jar her other proof jobs need
        if (!idx) { dispatch({ type: "NOTE", text: "Not yet - you don't have the right jars for that." }); return; }
        const jars = idx.map((i) => sRef.current.jars[i]);
        sRef.current = { ...sRef.current, jars: sRef.current.jars.filter((_, i) => !idx.includes(i)) };   // a double click can't hand them twice
        dispatch({ type: "HAND_OVER", idx });
        dispatch({ type: "HAND_ADD", hand: { id: newHandId(), sid: c.sid, by: c.uid, kind: "DELIVER_JARS", ct: ct.id, jars, at: Date.now() } });
        return;
      }
      if (YARD_ACTIONS.includes(a.type)) {
        if (!c.ready) { dispatch({ type: "NOTE", text: `Hang on - walking over to ${c.keeper ? c.keeper.name : "your friend"}'s yard.`, icon: "still" }); return; }
        send(a).catch(() => dispatch({ type: "NOTE", text: "Couldn't reach your friend's yard.", icon: "still" }));
        return;
      }
    }
    if (c.mode === "keeper" && a.type === "BLEND") { dispatch({ ...a, share: c.others }); return; }
    dispatch(a);
  }, [dispatch]);

  return { mode, keeperName: keeper ? keeper.name : null, keeperUid: keeper ? keeper.uid : null, ready: !!remote, helpers: Math.max(0, crew.length - 1), view, act };
}
